<template>
  <div>
    <!-- ── Bottom sheet mobile ───────────────────────────────── -->
    <div v-if="cartSheet" class="cart market-view cart--sheet">
      <div class="sheet-tabs" role="tablist">
        <button
            class="sheet-tab"
            role="tab"
            :aria-selected="cartSection == 'bet'"
            :class="{ active: cartSection == 'bet' }"
            @click="switchCartSection('bet')"
        >
          <span>{{ $t('cartSheet.betSlip') }}</span>
          <span class="tab-badge" v-if="effectiveOddsCount > 0">{{ effectiveOddsCount }}</span>
        </button>
        <button
            class="sheet-tab"
            role="tab"
            :aria-selected="cartSection == 'myBets'"
            :class="{ active: cartSection == 'myBets' }"
            @click="switchCartSection('myBets')"
        >
          <span>{{ $t('myBets') }}</span>
        </button>
        <span class="sheet-tabs-ink" :class="{ 'is-right': cartSection == 'myBets' }"></span>
      </div>

      <transition name="sheet-section" mode="out-in">
        <div class="sheet-section" v-if="cartSection == 'bet'" key="bet">
          <BetSlipMarketView></BetSlipMarketView>
          <GetBookedBet v-if="getBet.odds.length == 0" class="sheet-booked-bet" />
        </div>
        <div class="sheet-section" v-else key="myBets">
          <MyBets></MyBets>
        </div>
      </transition>

      <template v-if="showSheetFooter">
        <!-- Spazio riservato: il footer è ancorato al fondo della modale, fuori dallo scroll -->
        <div class="sheet-footer-spacer" :style="{ height: sheetFooterHeight + 'px' }"></div>
        <div class="sheet-footer" ref="sheetFooter">
          <ActionsMarketView></ActionsMarketView>
        </div>
      </template>
    </div>

    <!-- ── Colonna destra desktop ────────────────────────────── -->
    <div v-else class="cart market-view">
      <div class="cart_container">
        <div class="cart_header" :class="{ 'cart_header--closed': !cartOpen }" @click="onHeaderClick">
          <div class="cart-tabs" @click.stop>
            <div class="item" :class="cartSection == 'bet' ? 'active' : 'deactivated'" @click="switchCartSection('bet')">
              <span class="tab-label">{{$t('bet')}}</span>
              <span class="tab-badge" v-if="effectiveOddsCount > 0">{{effectiveOddsCount}}</span>
            </div>
            <div class="item" :class="cartSection == 'myBets' ? 'active' : 'deactivated'" @click="switchCartSection('myBets')">
              <span class="tab-label">{{$t('myBets')}}</span>
            </div>
          </div>
          <div class="pa-3">
            <svg
                class="cart-chevron"
                :class="{ 'cart-chevron--open': cartOpen }"
                width="14" height="14" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="2.5"
                stroke-linecap="round" stroke-linejoin="round"
            >
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>

        </div>
        <transition name="cart-collapse">
          <div v-show="cartOpen">
            <div class="cart-content" v-if="cartSection == 'bet'">
              <BetSlipMarketView></BetSlipMarketView>
            </div>
            <div class="cart-content" v-if="cartSection == 'myBets'">
              <MyBets></MyBets>
            </div>
          </div>


        </transition>
        <ActionsMarketView v-if="!getConfirmedBet && !hasAcceptanceBets && cartSection == 'bet'"></ActionsMarketView>

      </div>
      <!-- Codice prenotato: solo nella sezione schedina e a carrello vuoto -->
      <GetBookedBet v-if="cartSection == 'bet' && getBet.odds.length == 0" class="desktop-booked-bet" />
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import MyBets from "@/components/right_widgets/MyBets";
import BetSlipMarketView from "@/components/right_widgets/BetSlipMarketView";
import { isMobileOnly } from 'mobile-device-detect';
import ActionsMarketView from "@/components/right_widgets/ActionsMarketView.vue";
import GetBookedBet from "@/components/right_widgets/GetBookedBet";
import { postToParent, PARENT_EVENTS } from "@/services/parentBridge/parentBridge";
import { getPreference, setPreference, UI_PREFERENCES } from "@/services/uiPreferences";

export default {
  name: 'CartMarketView',
  components: {ActionsMarketView, MyBets, BetSlipMarketView, GetBookedBet},
  inject: { cartSheetHost: { default: false } },
  // Re-provided as `cartSheet` so only the classic cart subtree switches to
  // the sheet layout, not the American cart that shares the same host.
  provide() {
    return { cartSheet: this.cartSheetHost };
  },
  data: () => ({
    cartSection: "bet",
    other : 'item cart-button',
    cartOpen: true,
    sheetFooterHeight: 0,
  }),
  computed: {
    ...mapGetters({
      'getLanguage': 'settings/getLanguage',
      'getBet': 'cart/getBet',
      'getLoading': 'cart/getLoading',
      'getMobileCartOpen': 'cart/getMobileCartOpen',
      'getConfirmedBet':          'cart/getConfirmedBet',
      'getAcceptingBets':         'cart/getAcceptingBets',
      'getAcceptedBetWithChange': 'cart/getAcceptedBetWithChange',
      'getAuthenticated':         'user/getAuthenticated',
    }),
    cartSheet() {
      return this.cartSheetHost;
    },
    showSheetFooter() {
      return !this.getConfirmedBet && !this.hasAcceptanceBets && this.cartSection == 'bet' && this.getBet.odds.length > 0;
    },
    hasAcceptanceBets() {
      return (this.getAcceptingBets || []).length > 0 || (this.getAcceptedBetWithChange || []).length > 0;
    },
    effectiveOddsCount() {
      const odds = this.getBet.odds;
      const bbIds = new Set(odds.filter(o => o.bet_builder_id).map(o => o.bet_builder_id));
      return odds.filter(o => !o.bet_builder_id).length + bbIds.size;
    },
  },
  methods: {
    ...mapActions({
      'setLoadingCart': 'cart/setLoadingCart',
    }),

    onHeaderClick() {
      if (isMobileOnly) {
        this.$root.$emit('close_sidebar_right', true);
      } else {
        this.cartOpen = !this.cartOpen;
        setPreference(UI_PREFERENCES.SLIP_OPEN, this.cartOpen);
        // Parent bridge: the mobile cart is posted by the store plugin, the desktop one from here.
        postToParent(PARENT_EVENTS.CART_STATE_CHANGED, { isOpen: this.cartOpen });
      }
    },

    // Il footer cambia altezza (avvisi, errori): lo spaziatore lo segue.
    observeSheetFooter() {
      const el = this.$refs.sheetFooter;
      if (el === this._observedFooter) return;
      if (this._footerObserver) this._footerObserver.disconnect();
      this._observedFooter = el;
      if (!el || typeof ResizeObserver === 'undefined') return;
      this._footerObserver = new ResizeObserver(() => {
        this.sheetFooterHeight = el.offsetHeight;
      });
      this._footerObserver.observe(el);
    },

    switchCartSection(section){
      this.cartSection = section;
      this.$root.$emit('cart-section-change', section);
    },

  },

  watch: {
    // Logged in with an empty slip there is nothing to place: open on My Bets.
    getMobileCartOpen(isOpen) {
      if (!isOpen || !this.cartSheet) return;
      const nothingToShow = this.effectiveOddsCount === 0 && !this.getConfirmedBet && !this.hasAcceptanceBets;
      this.switchCartSection(this.getAuthenticated && nothingToShow ? 'myBets' : 'bet');
    },
    effectiveOddsCount(count, previous) {
      if (this.cartSheet && previous === 0 && count > 0 && this.cartSection !== 'bet') {
        this.switchCartSection('bet');
      }
    },
  },

  created() {
    // The desktop column opens as the player left it; the mobile sheet has its own open state
    if (!isMobileOnly && !this.cartSheet) {
      this.cartOpen = getPreference(UI_PREFERENCES.SLIP_OPEN);
    }
  },

  mounted() {
    // Kept to remove exactly this handler: other components listen to sectionCart too
    this._onSectionCart = () => this.switchCartSection('bet');
    this.$root.$on('sectionCart', this._onSectionCart);
    this.observeSheetFooter();
  },

  updated() {
    this.observeSheetFooter();
  },

  beforeDestroy() {
    this.$root.$off('sectionCart', this._onSectionCart);
    if (this._footerObserver) this._footerObserver.disconnect();
  },

};
</script>

<style scoped>
/* ── Card shell ──────────────────────────────────────────── */
.v-card.cart.market-view {
    background-color: var(--bg-header);
    border-radius: 8px;
    box-shadow: none !important;
    overflow: hidden;
}

/* ── Tabs inside header ──────────────────────────────────── */
.cart-tabs {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: 6px;
  gap: 6px;
}

.cart-tabs .item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  font-size: var(--font-size-xl);
  border-radius: 5px;
  font-weight: 500;
  cursor: pointer;
  color: var(--text-op-50-platform);
  transition: background 0.15s, color 0.15s;
  user-select: none;
  white-space: nowrap;
  padding: 12px 0 12px 12px;
}

.cart-tabs .item.active {
  color: var(--text-platform);
  font-weight: bolder;
}

.cart-tabs .item--hidden {
  opacity: 0.3;
  cursor: default;
  pointer-events: none;
  display: none;
}



/* ── Tab label ───────────────────────────────────────────── */
.tab-label {
    line-height: 1;
}

/* ── Badge numero quote ──────────────────────────────────── */
.tab-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--color-primary-light);
    color: var(--bg-body);
    font-size: var(--font-size-xs);
    font-weight: 700;
    border-radius: 9px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    line-height: 1;
}

/* ── Content wrapper ─────────────────────────────────────── */
.cart-content {
  background: var(--sidebar);
  border-radius: 0 0 8px 8px;
}

.cart_container{

}
.cart_container .cart_header{
  background: var(--bg-card);
  border-radius: 8px 8px 0 0;
  padding: 0;
  font-size: var(--font-size-4xl);
  font-weight: 700;
  line-height: 15px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
}

.cart_header--closed {
  border-radius: 8px !important;
}

.cart-chevron {
  transition: transform 0.25s ease;
  flex-shrink: 0;
  color: var(--text-platform);
}

.cart-chevron--open {
  transform: rotate(180deg);
}

/* Collapse transition */
.cart-collapse-enter-active,
.cart-collapse-leave-active {
  transition: max-height 0.3s ease, opacity 0.25s ease;
  overflow: hidden;
  max-height: 2000px;
}

.cart-collapse-enter,
.cart-collapse-leave-to {
  max-height: 0;
  opacity: 0;
}
.cart_container .cart-content {
  padding: 10px  ;

}

.cart.market-view{
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Bottom sheet mobile ─────────────────────────────────── */
.cart--sheet {
  gap: 0;
}

.sheet-tabs {
  position: sticky;
  top: 0;
  z-index: 3;
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin: 0 -16px;
  padding: 0 16px;
  background: var(--sidebar);
  border-bottom: 1px solid var(--border-color, rgba(255,255,255,0.08));
}

.sheet-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  border: none;
  background: transparent;
  font-size: var(--font-size-2xl);
  font-weight: 700;
  text-transform: uppercase;
  color: var(--text-op-50-platform);
  cursor: pointer;
  transition: color 0.2s;
}

.sheet-tab.active {
  color: var(--text-platform);
}

.sheet-tabs-ink {
  position: absolute;
  bottom: -1px;
  left: 16px;
  width: calc(50% - 16px);
  height: 3px;
  border-radius: 3px 3px 0 0;
  background: var(--color-primary-light);
  transition: transform 0.3s cubic-bezier(0.32,0.72,0,1);
}

.sheet-tabs-ink.is-right {
  transform: translateX(100%);
}

.sheet-section {
  padding-top: 14px;
}

.sheet-booked-bet {
  margin-top: 20px;
}

.desktop-booked-bet {
  margin-top: 20px;
}

.sheet-section-enter-active,
.sheet-section-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.sheet-section-enter {
  opacity: 0;
  transform: translateY(6px);
}
.sheet-section-leave-to {
  opacity: 0;
}

/* Il bottone "piazza scommessa" è ancorato al fondo della modale (fuori
   dallo scroll: nessun antenato posizionato fino a .side-modal.cart) */
.sheet-footer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  padding: 12px 16px 16px;
  background: var(--bg-body);
  box-shadow: 0 -10px 20px -12px rgba(0, 0, 0, 0.5);
}

@media (max-width: 600px) {
  .cart-tabs .item {
    font-size: var(--font-size-4xl);
  }

}

</style>